import { c as _c } from "react/compiler-runtime";
import { formatCents } from 'betterplace-utils';
import { Expandable, Fieldset, Heading, LayoutStack, ToggleButton, useIsMounted } from 'design-system/client';
import { useTranslations, useLocale } from 'next-intl';
import analytics, { getDonationFormType } from 'product-analytics';
import { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import styles from './AmountSelection.module.css';
import { useDonationFormConfig, useDonationFormContext } from '@/donationPages/_dependencies/helpers';
import DonationAmountField, { AMOUNT_FIELD_NAME } from '@/donationPages/iframe/[receiver-type]/[slug]/_dependencies/IFrameForm/DonationAmountField/DonationAmountField';
const predefinedAmountsInCents = [2500, 5000, 10000, 20000];
type PredefinedAmountsProps = {
  value: number;
  customAmountEnabled: boolean;
  onChange: (value: number) => void;
  setCurrentValue: (value: number) => void;
  enableCustomAmount: (value: boolean) => void;
};
function PredefinedAmount_(t0) {
  const $ = _c(13);
  const {
    amountInCents,
    value,
    customAmountEnabled,
    onChange: onChange_,
    setCurrentValue,
    enableCustomAmount
  } = t0;
  const locale = useLocale();
  const {
    getValues
  } = useDonationFormContext();
  let t1;
  if ($[0] !== amountInCents || $[1] !== locale) {
    t1 = formatCents(locale, amountInCents);
    $[0] = amountInCents;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const label = t1;
  let t2;
  if ($[3] !== enableCustomAmount || $[4] !== getValues || $[5] !== onChange_ || $[6] !== setCurrentValue) {
    t2 = event => {
      onChange_(Number(event.target?.value));
      enableCustomAmount(false);
      setCurrentValue(getValues(AMOUNT_FIELD_NAME));
    };
    $[3] = enableCustomAmount;
    $[4] = getValues;
    $[5] = onChange_;
    $[6] = setCurrentValue;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const onChange = t2;
  const t3 = amountInCents === value && !customAmountEnabled;
  let t4;
  if ($[8] !== amountInCents || $[9] !== label || $[10] !== onChange || $[11] !== t3) {
    t4 = <ToggleButton key={amountInCents} name={AMOUNT_FIELD_NAME} type="radio" value={amountInCents} checked={t3} className={styles.radio} onChange={onChange} fullWidth="mobile-only">{label}</ToggleButton>;
    $[8] = amountInCents;
    $[9] = label;
    $[10] = onChange;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
const PredefinedAmount = memo(PredefinedAmount_);
function PredefinedAmounts_(props) {
  const $ = _c(4);
  let t0;
  if ($[0] !== props) {
    t0 = predefinedAmountsInCents.map(amountInCents => <PredefinedAmount key={amountInCents} amountInCents={amountInCents} {...props} />);
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <>{t0}</>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
const PredefinedAmounts = memo(PredefinedAmounts_);
function AmountSelection() {
  const $ = _c(44);
  const t = useTranslations("nextjs");
  const {
    formState: t0,
    control,
    getValues
  } = useDonationFormContext();
  const {
    errors
  } = t0;
  const {
    receiverId,
    receiverType,
    channel,
    showCodonation,
    defaultDonationCents
  } = useDonationFormConfig();
  let t1;
  if ($[0] !== getValues) {
    t1 = getValues(AMOUNT_FIELD_NAME);
    $[0] = getValues;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [currentValue, setCurrentValue] = useState(t1);
  const previousValue = useRef(currentValue);
  let t2;
  if ($[2] !== defaultDonationCents) {
    t2 = predefinedAmountsInCents.includes(defaultDonationCents);
    $[2] = defaultDonationCents;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [customAmountEnabled, enableCustomAmount] = useState(!t2);
  const mounted = useIsMounted();
  let t3;
  let t4;
  if ($[4] !== channel || $[5] !== currentValue || $[6] !== customAmountEnabled || $[7] !== receiverId || $[8] !== receiverType || $[9] !== showCodonation) {
    t3 = () => {
      if (currentValue === previousValue.current) {
        return;
      }
      analytics.donationAmountChanged({
        id: receiverId,
        receiver_type: receiverType,
        donation_form_type: getDonationFormType({
          receiverId,
          receiverType,
          channel,
          showCodonation
        }),
        previous_donation_amount: previousValue.current,
        new_donation_amount: currentValue,
        selection_type: customAmountEnabled ? "donation_own_amount" : "donation_amount_button"
      });
      previousValue.current = currentValue;
    };
    t4 = [channel, currentValue, customAmountEnabled, receiverId, receiverType, showCodonation];
    $[4] = channel;
    $[5] = currentValue;
    $[6] = customAmountEnabled;
    $[7] = receiverId;
    $[8] = receiverType;
    $[9] = showCodonation;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== t) {
    t5 = t("donate.attributes.amount");
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t5) {
    t6 = <Heading level="heading-xs" className={styles.heading}>{t5}</Heading>;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = errors[AMOUNT_FIELD_NAME]?.message;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      alignment: "justified",
      space: "100"
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== customAmountEnabled) {
    t9 = t10 => {
      const {
        field: t11
      } = t10;
      const {
        onChange,
        value
      } = t11;
      return <PredefinedAmounts value={value} customAmountEnabled={customAmountEnabled} onChange={onChange} setCurrentValue={setCurrentValue} enableCustomAmount={enableCustomAmount} />;
    };
    $[17] = customAmountEnabled;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== control || $[20] !== t9) {
    t10 = <Controller control={control} name={AMOUNT_FIELD_NAME} render={t9} />;
    $[19] = control;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== customAmountEnabled || $[23] !== mounted || $[24] !== t) {
    t11 = t12 => {
      const {
        field: t13
      } = t12;
      const {
        onChange: onChange_0
      } = t13;
      return <ToggleButton className={styles.button} type="checkbox" onChange={event => {
        onChange_0(event);
        enableCustomAmount(true);
      }} disabled_={!mounted} checked={customAmountEnabled}>{t("donate.amount_selection.custom_amount")}</ToggleButton>;
    };
    $[22] = customAmountEnabled;
    $[23] = mounted;
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== control || $[27] !== t11) {
    t12 = <Controller control={control} name="_custom_amount" render={t11} />;
    $[26] = control;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== control || $[30] !== customAmountEnabled || $[31] !== getValues) {
    t13 = customAmountEnabled && <Controller control={control} name={AMOUNT_FIELD_NAME} render={() => <div className={styles.inputWrapper}><DonationAmountField className={styles.input} hideError={true} required={true} onBlur={() => {
        setCurrentValue(getValues(AMOUNT_FIELD_NAME));
      }} /></div>} />;
    $[29] = control;
    $[30] = customAmountEnabled;
    $[31] = getValues;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== customAmountEnabled || $[34] !== t13) {
    t14 = <Expandable open={customAmountEnabled} contentClassName={styles.flex}>{t13}</Expandable>;
    $[33] = customAmountEnabled;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t12 || $[37] !== t14) {
    t15 = <LayoutStack>{t12}{t14}</LayoutStack>;
    $[36] = t12;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t10 || $[40] !== t15 || $[41] !== t6 || $[42] !== t7) {
    t16 = <Fieldset legend={t6} error={t7} errorDisplay="text" className={styles.fieldset} layoutProps={t8}>{t10}{t15}</Fieldset>;
    $[39] = t10;
    $[40] = t15;
    $[41] = t6;
    $[42] = t7;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
}
export default memo(AmountSelection);